/**
 * @fileoverview JkrangeComponent - 區間選擇元件
 * @description
 * 此元件提供一個區間選擇功能，允許用戶設置起始和結束值。
 * 支援各種資料類型，包括字串、數字、日期等。
 *
 * @author JK Team
 * @version 1.0.0
 */
import {
  Component,
  Input,
  OnInit,
  output,
  input,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { JkbtneditComponent } from '../jkbtnedit/jkbtnedit.component';
import { JkBaseComponent, JkfieldlabelComponent, MaterialModule } from 'jk-service';
import { jkLib } from 'jk-service';
import { CommonModule, NgStyle } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';

/**
 * @component JkrangeComponent
 * @description
 * 區間選擇元件，提供設置起始值和結束值的功能。
 * 此元件基於JkbtneditComponent實現開窗選擇，適用於各種資料類型。
 * @extends {JkBaseComponent}
 */
@Component({
  selector: 'app-jkrange',
  templateUrl: './jkrange.component.html',
  styleUrls: ['./jkrange.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [JkbtneditComponent,    MaterialModule,
    CommonModule,
    NgStyle,
    FormsModule,
    JkfieldlabelComponent,
],
})
export class JkrangeComponent extends JkBaseComponent implements OnInit {
  /**
   * @property {string} comType - 元件類型
   * @override
   */
  override comType: string = 'jkrange';

  /**
   * @property {string|number|null} editWidth - 編輯欄位寬度
   * @description 根據元件寬度計算編輯欄位寬度
   */
  get editWidth(): string|number {
    const w = this.width();
    if(typeof w === 'number'){
      return w - 10;
    }
    return w;
  };
  /**
   * @property {JkbtneditComponent} edA - 起始值編輯元件引用
   */
  readonly edA = viewChild<JkbtneditComponent>('edA');

  /**
   * @property {JkbtneditComponent} edB - 結束值編輯元件引用
   */
  readonly edB = viewChild<JkbtneditComponent>('edB');

  /**
   * @property {string} _dataType - 資料類型
   * @private
   */
  _dataType: string = 'string'; //  string, text, number, datefield,timefield

  /**
   * @property {boolean} showOpenWinButton - 是否顯示開窗按鈕
   */
  readonly showOpenWinButton = input<boolean>(true);
  /**
   * @property {string} dataType - 設置資料類型
   * @description 可接受的值: string, text, number, datefield, timefield
   */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set dataType(value: string) {
    this._dataType = value;
  }

  /**
   * @property {string} maintable - 主表名稱
   */
  readonly maintable = input<string>('');

  /**
   * @property {string} item - 項目名稱
   */
  readonly item = input<string>('');

  /**
   * @property {string} valueExpr - 值表達式
   */
  readonly valueExpr = input<string>('id');

  /**
   * @property {string} displayExpr - 顯示表達式
   */
  readonly displayExpr = input<string>('name');

  /**
   * @property {Array<any>} params - 參數陣列
   */
  readonly params = input<Array<any>>([]);
  /**
   * @property {string} titleA - 起始值標題
   * @returns {string} 返回起始值標題文字
   */
  get titleA() {
    return '起';
  }

  /**
   * @property {string} titleB - 結束值標題
   * @returns {string} 返回結束值標題文字
   */
  get titleB() {
    return '迄';
  }

  /**
   * @property {string} _valueA - 起始值內部存儲
   * @private
   */
  _valueA: string = '';

  /**
   * @property {string} valueA - 起始值
   * @description 設置起始值，並同步更新到編輯元件
   */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set valueA(value: string) {
    if (this._valueA !== value)
      // 避免同一週期更新數次
      this._valueA = value;
    const edA = this.edA();
    if (edA)
      if (edA.value !== value)
        // 避免同一週期更新數次
        edA.value = value;
    /*if(this.dataType==='datefield'){
        if(value==='' || value===null){
          this.dateA = undefined as any;  // TODO: need test
        }else{
          let d = jkLib.dateField2Date(value);
          if(this.dateA !== d){ // 避免無限迴圈
            this.dateA = d;
          }
        }
      }*/
  }
  get valueA() {
    const edA = this.edA();
    if (edA) return edA.value;
    else return '';
  }
  /**
   * @property {EventEmitter<Event>} valueAChange - 起始值變更事件
   */
  readonly valueAChange = output<Event>();

  /**
   * 當起始值變更時觸發
   * @param {Event} event - 事件物件
   */
  onValueAChange(event: Event) {
    const edA = this.edA();
    if (edA) {
      if (edA.value !== this.valueA) {
        // 避免同一週期更新數次
        edA.value = this.valueA;
        this.valueAChange.emit(event);
      }
    }
  }

  /**
   * @property {string} _valueB - 結束值內部存儲
   * @private
   */
  _valueB: string = '';

  /**
   * @property {string} valueB - 結束值
   * @description 設置結束值，並同步更新到編輯元件
   */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set valueB(value: string) {
    if (this._valueB !== value)
      // 避免同一週期更新數次
      this._valueB = value;
    const edB = this.edB();
    if (edB)
      if (edB.value !== value)
        // 避免同一週期更新數次
        edB.value = value;

  }
  get valueB(): string {
    const edB = this.edB();
    if (edB) return edB.value;
    else return '';
  }
  /**
   * @property {EventEmitter<Event>} valueBChange - 結束值變更事件
   */
  readonly valueBChange = output<Event>();

  /**
   * 當結束值變更時觸發
   * @param {Event} event - 事件物件
   */
  onValueBChange(event: Event) {
    const edB = this.edB();
    if (edB && edB.value !== this.valueB) {
      // 避免同一週期更新數次
      edB.value = this.valueB;
      this.valueBChange.emit(event);
    }
  }
  /*
  // dataType datefield
  private _dateA: Date | undefined;
  get dateA():Date | undefined{
    return this._dateA;
  }
  set dateA(d:Date){
    this._dateA = d;
    let v = jkLib.date2DateField(d);
    if(this.valueA !== v){ // 避免無限迴圈
      //alert('set date: ' + v);
      this.valueA = v;
    }
  }
  _dateB: Date | undefined;
  get dateB():Date | undefined{
    return this._dateB;
  }
  set dateB(d:Date){
    this._dateB = d;
    let v = jkLib.date2DateField(d);
    if(this.valueB !== v){ // 避免無限迴圈
      //alert('set date: ' + v);
      this.valueB = v;
    }
  }
  */
  /**
   * 建構函式
   */
  constructor() {
    super();
  }


  /**
   * 初始化函式
   * @override
   */
  override ngOnInit() {
    super.ngOnInit();
  }

  /**
   * 視圖初始化後執行
   * @override
   */
  override ngAfterViewInit() {
    super.ngAfterViewInit();
    const edA = this.edA();
    if(edA){
      edA.maintable = this.maintable();
      edA.item = this.item();
      edA.valueExpr = this.valueExpr();
      edA.displayExpr = this.displayExpr();
    }
    const edB = this.edB();
    if(edB){
      edB.maintable = this.maintable();
      edB.item = this.item();
      edB.valueExpr = this.valueExpr();
      edB.displayExpr = this.displayExpr();
    }
    setTimeout(() => {
      /* 使用 setTimeout() 方法來延遲修改屬性的代碼。
      setTimeout() 會將你的代碼排入事件隊列，並在當前的變更檢測週期結束後執行。*/
      const edAValue = this.edA();
      if (edAValue)
        if (edAValue.value !== this._valueA) edAValue.value = this._valueA;
      const edBValue = this.edB();
      if (edBValue)
        if (edBValue.value !== this._valueB) edBValue.value = this._valueB;
    }, 500);
  }
  /**
   * 在視圖內容初始化後執行
   */
  ngAfterContentInit() {
    //let a = this.edA;
    /*if(this.edA)
      this.edA.value = this._valueA;
    if(this.edB)
      this.edB.value = this._valueB;*/
  }

  /**
   * 在視圖檢查後執行
   */
  ngAfterViewChecked() {}

  /**
   * 新增 Where 條件
   * @param {string} where - 現有的 where 條件
   * @param {string} fieldName - 欄位名稱
   * @returns {string} - 新的 where 條件
   */
  addWhere(where: string, fieldName: string): string {
    if (this.required() && (this.valueA === '' || this.valueB === '')) {
      let msg = this.label() + ' 不可空白';
      alert(msg);
      throw new Error(msg);
    }
    where = jkLib.range2Where(this.valueA, this.valueB, where, fieldName);
    return where;
  }
}
